import React, { FC, useEffect, useState } from 'react';
import { Button, FloatingBubble, Image, SearchBar, Selector, Space } from 'antd-mobile';
import { SendOutline, UndoOutline, AddCircleOutline } from 'antd-mobile-icons';
import { ActionSheet } from 'antd-mobile';
import type { Action } from 'antd-mobile/es/components/action-sheet';
import { useNavigate } from 'react-router-dom';
import Webcam from 'react-webcam';

// Import from new structure
import { useNavbar } from '../../../hooks/use-navbar';
import { ClientJson } from '../../../types/models';
import { ClientList } from '../clients';
import { faroLogger } from '../../../observability/faro-logger';

enum Kind {
  Immat = 'immat',
  ParcNumber = 'parc-number',
}

function dataURLtoFile(dataurl: string, filename: string): File {
  const arr = dataurl.split(',');
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const mime = arr[0].match(/:(.*?);/)![1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
}

const uploadPhoto = async (data: string, kind: Kind) => {
  const formData = new FormData();
  formData.append('file', dataURLtoFile(data, 'plate.png'));
  formData.append('kind', kind);

  faroLogger.info('photo_upload_started', {
    kind: kind,
  });

  try {
    const result = await fetch(`${process.env.REACT_APP_BACKEND_HOST}/api2/plates/upload`, {
      headers: { version: '' + process.env.REACT_APP_VERSION },
      body: formData,
      method: 'POST',
    }).then((res) => res.json());

    faroLogger.info('photo_upload_success', {
      kind: kind,
      candidatesCount: result?.length || 0,
    });

    return result;
  } catch (error) {
    console.error(error);
    faroLogger.error('photo_upload_failed', {
      kind: kind,
      error: error instanceof Error ? error.message : 'Unknown error',
    });
    return null;
  }
};

interface PlateCandidate {
  plate: string;
  kind: Kind;
  id?: number;
  name?: string;
  display_name?: string;
  vehicle_type?: string;
  filename: string;
}

const PhotoIcon: FC<{ name: string; size?: number }> = ({ name, size = 26 }) => {
  const props = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  if (name === 'plate') {
    return (
      <svg {...props}>
        <rect x="3" y="7" width="18" height="10" rx="1.5" />
        <line x1="7" y1="11" x2="7" y2="13" />
        <line x1="11" y1="11" x2="11" y2="13" />
        <line x1="15" y1="11" x2="15" y2="13" />
        <line x1="19" y1="11" x2="19" y2="13" />
      </svg>
    );
  }
  if (name === 'hash') {
    return (
      <svg {...props}>
        <line x1="4" y1="9" x2="20" y2="9" />
        <line x1="4" y1="15" x2="20" y2="15" />
        <line x1="10" y1="3" x2="8" y2="21" />
        <line x1="16" y1="3" x2="14" y2="21" />
      </svg>
    );
  }
  if (name === 'arrow-right') {
    return (
      <svg {...props} strokeWidth={2.2}>
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="12 5 19 12 12 19" />
      </svg>
    );
  }
  return null;
};

const PhotoKindChooser: FC<{ onPick: (k: Kind) => void }> = ({ onPick }) => {
  return (
    <div style={{ padding: '14px 14px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div
        style={{
          fontSize: 16,
          fontWeight: 700,
          color: 'var(--text-primary)',
          margin: '4px 0 4px',
          letterSpacing: '-0.01em',
        }}
      >
        Que voulez-vous photographier ?
      </div>
      <div
        style={{
          fontSize: 12,
          color: 'var(--text-secondary)',
          marginBottom: 6,
          lineHeight: 1.45,
        }}
      >
        Cadrez bien le sujet, sans flou, sans reflets. L&rsquo;app reconnaît automatiquement le camion s&rsquo;il a déjà été saisi.
      </div>

      <button
        type="button"
        onClick={() => onPick(Kind.Immat)}
        style={{
          background: 'rgba(49,220,200,0.07)',
          border: '1px solid rgba(49,220,200,0.45)',
          borderRadius: 16,
          padding: '18px',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          cursor: 'pointer',
          textAlign: 'left',
          color: 'var(--text-primary)',
          fontFamily: 'inherit',
          width: '100%',
        }}
      >
        <span
          style={{
            width: 50,
            height: 50,
            borderRadius: 13,
            background: 'rgba(49,220,200,0.14)',
            color: 'var(--teal)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <PhotoIcon name="plate" size={28} />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span
            style={{
              display: 'block',
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'var(--teal)',
              marginBottom: 4,
            }}
          >
            Recommandé
          </span>
          <div
            style={{
              fontSize: 17,
              fontWeight: 800,
              letterSpacing: '-0.015em',
              lineHeight: 1.15,
              marginBottom: 3,
            }}
          >
            Plaque d&rsquo;immatriculation
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', lineHeight: 1.4 }}>
            Plaque jaune ou blanche, à l&rsquo;avant ou à l&rsquo;arrière du camion
          </div>
        </span>
        <span style={{ flexShrink: 0, color: 'var(--teal)' }}>
          <PhotoIcon name="arrow-right" size={20} />
        </span>
      </button>

      <button
        type="button"
        onClick={() => onPick(Kind.ParcNumber)}
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 16,
          padding: '18px',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          cursor: 'pointer',
          textAlign: 'left',
          color: 'var(--text-primary)',
          fontFamily: 'inherit',
          width: '100%',
        }}
      >
        <span
          style={{
            width: 50,
            height: 50,
            borderRadius: 13,
            background: 'var(--bg-card-strong)',
            color: 'var(--text-primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <PhotoIcon name="hash" size={26} />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 17,
              fontWeight: 800,
              letterSpacing: '-0.015em',
              lineHeight: 1.15,
              marginBottom: 3,
            }}
          >
            Numéro de parc
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', lineHeight: 1.4 }}>
            Numéro peint ou collé sur la cabine ou la remorque
          </div>
        </span>
        <span style={{ flexShrink: 0, color: 'var(--text-faint)' }}>
          <PhotoIcon name="arrow-right" size={20} />
        </span>
      </button>

      <div
        style={{
          marginTop: 6,
          textAlign: 'center',
          fontSize: 11.5,
          color: 'var(--text-muted)',
          lineHeight: 1.4,
        }}
      >
        En cas de doute, prenez la <b style={{ color: 'var(--teal)', fontWeight: 700 }}>plaque d&rsquo;immat</b> — c&rsquo;est plus fiable.
      </div>
    </div>
  );
};

const Photo: FC = () => {
  const navbar = useNavbar();
  const navigate = useNavigate();

  useEffect(() => {
    if (navbar) {
      navbar.setTitle('Photos');
      navbar.setBackArrow(false);
      navbar.setRight();
    }
    faroLogger.info('photo_page_opened');
  }, [navbar]);

  const [kindOfPlate, setKindOfPlate] = useState<Kind | null>(null);
  // We don't need these state variables anymore, removing them
  const [candidates, setCandidates] = useState<PlateCandidate[]>([]);
  const [visible, setVisible] = useState(false);
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [selectedPlate, setSelectedPlate] = useState<PlateCandidate>();
  const [clientSearch, setClientSearch] = useState('');

  // const startCam = () => {
  //   setIsShowVideo(true);
  // };

  // const stopCam = () => {
  //   if (webcamRef && webcamRef.current) {
  //     const stream = webcamRef.current.stream;
  //     const tracks = stream?.getTracks() || [];
  //     tracks.forEach((track) => track.stop());
  //     setIsShowVideo(false);
  //   }
  // };
  const validateCapture = async (capturedDataUri: string, kind: Kind): Promise<void> => {
    const result = await uploadPhoto(capturedDataUri, kind);
    setCandidates(result);
    setVisible(true);
  };

  const actions: Action[] = [
    ...candidates.map((c) => ({
      text: c.plate,
      key: c.plate,
      description: c.name,
      onClick: () => {
        console.log('load plate', c);
        faroLogger.info('photo_plate_candidate_selected', {
          plate: c.plate,
          kind: c.kind,
          hasClient: !!c.id,
          clientId: c.id,
          vehicleType: c.vehicle_type,
        });

        if (c.id) {
          navigate(`/client/${c.id}/new?${c.kind}=${c.plate}&type=${c.vehicle_type}&file=${c.filename}`);
        } else {
          setShowClientPicker(true);
          setSelectedPlate(c);
        }
      },
    })),
    {
      text: 'Reprendre une photo',
      key: 'retake',
      onClick: () => {
        faroLogger.info('photo_retake_clicked');
        setKindOfPlate(null);
        setCandidates([]);
        setVisible(false);
      },
    },
    {
      // Après reconnaissance, le camion peut avoir changé de client : on garde le n°/plaque
      // ET la photo, l'agent choisit juste le bon client. Sans photo reconnue → saisie pure.
      text: candidates.length ? 'Choisir un autre client' : 'Saisir à la main',
      key: 'manuel',
      onClick: () => {
        faroLogger.info('photo_manual_entry_clicked');
        setShowClientPicker(true);
        setSelectedPlate(candidates[0]);
      },
    },
  ];

  if (!kindOfPlate) {
    return <PhotoKindChooser onPick={(k) => {
      faroLogger.info('photo_kind_selected', { kind: k });
      setKindOfPlate(k);
    }} />;
  }
  const options = [
    {
      label: 'Immatriculation',
      value: Kind.Immat,
    },
    {
      label: 'Numéro de Parc',
      value: Kind.ParcNumber,
    },
  ];
  return (
    <div>
      {!showClientPicker && (
        <>
          <CameraScreenshot
            onValidateCapture={(dataUri) => validateCapture(dataUri, kindOfPlate)}
            onOpenCamera={() => {
              setVisible(false);
            }}
          />
          <Selector
            style={{
              '--border-radius': '100px',
              '--border': 'solid transparent 1px',
              '--checked-border': 'solid var(--adm-color-primary) 1px',
              '--padding': '15px 24px',
              margin: '15px',
            }}
            columns={2}
            options={options}
            value={[kindOfPlate]}
            onChange={(v) => {
              if (v.length) {
                setKindOfPlate(v[0]);
              }
            }}
          />
          <ActionSheet visible={visible} actions={actions} />
        </>
      )}
      {showClientPicker && (
        <>
          <SearchBar
            placeholder="Rechercher un client…"
            value={clientSearch}
            onChange={setClientSearch}
            style={{ padding: '10px 12px' }}
          />
          <ClientList
            search={clientSearch}
            onChooseClient={(client: ClientJson) => {
              if (selectedPlate) {
                navigate(
                  `/client/${client.id}/new?${kindOfPlate}=${selectedPlate?.plate}&file=${selectedPlate?.filename}`,
                );
              } else {
                navigate(`/client/${client.id}/new`);
              }
            }}
          />
        </>
      )}
    </div>
  );
};

const CameraScreenshot: FC<{ onOpenCamera: () => void; onValidateCapture: (dataUri: string) => void }> = ({
  onValidateCapture,
  onOpenCamera,
}) => {
  const [dataUri, setDataUri] = useState('');

  function handleTakePhotoAnimationDone(dataUri: string): void {
    console.log('takePhoto', dataUri);
    faroLogger.info('photo_captured', {
      dataLength: dataUri.length,
    });
    setDataUri(dataUri);
  }

  return (
    <div>
      {dataUri ? (
        <>
          <Image src={dataUri} fit="contain" />
          <div
            style={{
              position: 'absolute',
              bottom: '60px',
              width: '100%',
            }}
          >
            <Space justify="center" block>
              <Button
                loading="auto"
                onClick={() => {
                  setDataUri('');
                  onOpenCamera();
                }}
              >
                <UndoOutline />
                Reprendre
              </Button>
              <Button
                loading="auto"
                onClick={async () => {
                  await onValidateCapture(dataUri);
                }}
              >
                <SendOutline /> Envoyer
              </Button>
            </Space>
          </div>
        </>
      ) : (
        <Camera onCapture={handleTakePhotoAnimationDone} />
      )}
    </div>
  );
};

const Camera: FC<{ onCapture: (dataUri: string) => void }> = ({ onCapture }) => {
  const webcamRef = React.useRef<Webcam>(null);
  const [cameraError, setCameraError] = React.useState<string | null>(null);
  const videoConstraints = {
    width: 1024,
    height: 1024,
    facingMode: 'environment',
  };

  const capture = React.useCallback(() => {
    const imageSrc = webcamRef.current?.getScreenshot() || '';
    onCapture(imageSrc);
  }, [webcamRef]);

  if (cameraError) {
    return (
      <div
        style={{
          padding: '40px 24px',
          color: '#fff',
          background: '#000',
          minHeight: '100vh',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ fontSize: 64, lineHeight: 1 }}>📵</div>
        <div style={{ fontSize: 18, fontWeight: 800 }}>Caméra indisponible</div>
        <div style={{ fontSize: 14, lineHeight: 1.5, color: 'rgba(255,255,255,0.85)', maxWidth: 340 }}>
          L&rsquo;application n&rsquo;a pas accès à la caméra.
          <br />
          <br />
          <b style={{ color: '#31DCC8' }}>Sur iPhone :</b>
          <br />
          Réglages → Camion Net (ou Safari) → Caméra → <b>Autoriser</b>
          <br />
          puis fermer et rouvrir l&rsquo;app.
        </div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 8 }}>
          Détail : {cameraError}
        </div>
      </div>
    );
  }

  return (
    <>
      <Webcam
        ref={webcamRef}
        audio={false}
        height="100%"
        screenshotFormat="image/jpeg"
        width="100%"
        videoConstraints={videoConstraints}
        onUserMedia={() => {
          setCameraError(null);
          faroLogger.info('photo_camera_ok');
        }}
        onUserMediaError={(err: string | DOMException) => {
          const msg = err instanceof Error ? err.message : String(err);
          setCameraError(msg);
          faroLogger.error('photo_camera_error', { error: msg });
        }}
      />
      <FloatingBubble
        axis="x"
        magnetic="x"
        style={{
          '--size': '80px',
          '--initial-position-bottom': '84px',
          '--initial-position-right': '50%',
          '--edge-distance': '2px',
        }}
        onClick={capture}
        className="screenshot-btn"
      >
        <AddCircleOutline fontSize={22} />
      </FloatingBubble>
    </>
  );
};

export default Photo;
